import type {
  TrackingRoute,
  TrackingRouteStatus,
} from 'common/src/api/shipment-arrivals';
import { getShipmentCalendarDayOffset } from '../../../utils/shipment-logistics';
import type { Arrival } from './model';

export type LngLat = [number, number];
/** Urgent (arrives today/tomorrow), delayed vs. plan, or on time. */
export type MapTone = 'danger' | 'warning' | 'success';

export interface MapDestination {
  pod: string;
  name: string;
  flag: string;
  coordinates: LngLat | null;
  tone: MapTone;
  arrivals: Arrival[];
  kg: number;
  minOffset: number;
}

export interface MapVoyage {
  key: string;
  pod: string;
  /** Same vessel and voyage (or airline) to the same destination. */
  routeKey: string;
  routeLabel: string;
  originKey: string | null;
  /** Arrival rows (container × client) carried on this voyage. */
  arrivalIds: string[];
  tone: MapTone;
  label: string;
  origin: LngLat | null;
  destination: LngLat | null;
  /** Real route from the provider, by segment. Empty without a stored route. */
  segments: Array<{ coordinates: LngLat[]; status: TrackingRouteStatus }>;
  /** Transshipment ports or airports between origin and destination. */
  stops: LngLat[];
  /** Live position reported by the provider. */
  position: LngLat | null;
  /** Without a live route: share of the trip done, from ETD and ETA (0–1). */
  progress: number | null;
}

export interface MapOrigin {
  key: string;
  name: string;
  coordinates: LngLat;
}

/** What is picked on the map: a destination, an origin or a shared route. */
export interface MapFilter {
  kind: 'pod' | 'origin' | 'route';
  key: string;
  label: string;
}

export interface ArrivalMap {
  destinations: MapDestination[];
  voyages: MapVoyage[];
  origins: MapOrigin[];
  live: number;
  estimated: number;
}

// Ports and airports seen in tracking, for destinations and origins without a
// stored provider route yet. A stored route always takes precedence.
const PLACES: Record<string, LngLat> = {
  CLSAI: [-71.61, -33.59],
  CLVAP: [-71.63, -33.03],
  CLCNL: [-73.15, -36.73],
  SCL: [-70.79, -33.39],
  USPHL: [-75.14, 39.95],
  USLGB: [-118.21, 33.75],
  USLAX: [-118.27, 33.73],
  USSAV: [-81.09, 32.08],
  USNTD: [-119.21, 34.15],
  USFLL: [-80.12, 26.09],
  USHOU: [-95.27, 29.73],
  USNYC: [-74.04, 40.67],
  USILM: [-77.95, 34.24],
  USOAK: [-122.3, 37.8],
  USSEA: [-122.35, 47.6],
  CAVAN: [-123.1, 49.29],
  CAMTR: [-73.55, 45.5],
  MXZLO: [-104.31, 19.05],
  COBUN: [-77.03, 3.89],
  COCTG: [-75.53, 10.39],
  PABLB: [-79.57, 8.95],
  HNPCR: [-87.95, 15.84],
  BRSSZ: [-46.3, -23.96],
  ITLIV: [10.3, 43.55],
  ESVGO: [-8.72, 42.24],
  ESALG: [-5.44, 36.13],
  ESVLC: [-0.32, 39.45],
  DEHAM: [9.97, 53.54],
  NLRTM: [4.05, 51.95],
  BEANR: [4.4, 51.23],
  GBLGP: [0.48, 51.5],
  FRLEH: [0.1, 49.48],
  RULED: [30.2, 59.9],
  AEJEA: [55.03, 25.0],
  SAJED: [39.17, 21.48],
  INNSA: [72.95, 18.95],
  INCOK: [76.26, 9.97],
  INMUN: [69.7, 22.74],
  SGSIN: [103.85, 1.26],
  THLCH: [100.9, 13.08],
  VNSGN: [106.75, 10.77],
  IDJKT: [106.88, -6.1],
  MYPKG: [101.4, 3.0],
  PHMNL: [120.96, 14.6],
  HKHKG: [114.17, 22.3],
  CNSHA: [121.5, 31.23],
  CNNSA: [113.6, 22.75],
  CNSHK: [113.9, 22.48],
  CNYTN: [114.27, 22.57],
  CNTAO: [120.32, 36.07],
  CNNGB: [121.85, 29.93],
  CNXMN: [118.07, 24.48],
  TWKHH: [120.28, 22.6],
  KRPUS: [129.04, 35.1],
  JPTYO: [139.77, 35.62],
  MAD: [-3.57, 40.49],
  LHR: [-0.45, 51.47],
  AMS: [4.76, 52.31],
  FRA: [8.57, 50.03],
  PVG: [121.81, 31.14],
  HKG: [113.92, 22.31],
  CAN: [113.3, 23.39],
  BOM: [72.87, 19.09],
  BKK: [100.75, 13.69],
  SIN: [103.99, 1.36],
  ICN: [126.45, 37.46],
  NRT: [140.39, 35.77],
  DXB: [55.36, 25.25],
  MIA: [-80.29, 25.79],
  JFK: [-73.78, 40.64],
  LAX: [-118.41, 33.94],
  GRU: [-46.47, -23.43],
};

// Short names for the usual origins; provider names are long airport names.
const ORIGIN_NAMES: Record<string, string> = {
  CLSAI: 'SAN ANTONIO',
  CLVAP: 'VALPARAÍSO',
  CLCNL: 'CORONEL',
  SCL: 'SANTIAGO (SCL)',
};

/** Longitudes in [-180, 180]; provider flight paths may run past 180. */
export const wrapLongitude = (lng: number) =>
  lng >= -180 && lng <= 180
    ? lng
    : Math.round(((((lng + 180) % 360) + 360) % 360) * 1e6) / 1e6 - 180;
const wrap = ([lng, lat]: LngLat): LngLat => [wrapLongitude(lng), lat];
const place = (code: string | null | undefined) => {
  const key = (code ?? '').trim().toUpperCase();
  return PLACES[key] ? wrap(PLACES[key]) : null;
};

const MONTHS = 'ene feb mar abr may jun jul ago sept oct nov dic'.split(' ');
/** "2026-11-03" → "03 nov" */
const shortDate = (value: string) => {
  const [, month, day] = value.slice(0, 10).split('-').map(Number);
  return month && day
    ? `${String(day).padStart(2, '0')} ${MONTHS[month - 1]}`
    : value;
};

const toneOf = (arrivals: Arrival[]): MapTone =>
  arrivals.some((a) => a.urgent)
    ? 'danger'
    : arrivals.some((a) => (a.delayDays ?? 0) > 0)
      ? 'warning'
      : 'success';

/** Live position and route of one journey, from its stored provider route. */
function readRoute(route: TrackingRoute | undefined) {
  const features = route?.geojson.features ?? [];
  const points = features.flatMap((f) =>
    f.geometry.type === 'Point' && !('current' in f.properties)
      ? [
          {
            coordinates: wrap(f.geometry.coordinates),
            status: f.properties.status,
            code: f.properties.code ?? null,
          },
        ]
      : []
  );
  const segments = features.flatMap((f) =>
    f.geometry.type === 'LineString' && f.properties.status !== undefined
      ? [
          {
            coordinates: f.geometry.coordinates.map(wrap),
            status: f.properties.status,
            current:
              'current' in f.properties && f.properties.current
                ? wrap(f.properties.current)
                : null,
          },
        ]
      : []
  );
  const atPort = points.find((p) => p.status === 'CURRENT');
  const underway = segments.find((s) => s.current);
  const notDeparted =
    points.length > 0 && points.every((p) => p.status === 'FUTURE');
  return {
    origin: points[0]?.coordinates ?? null,
    originCode: points[0]?.code ?? null,
    stops: points.slice(1, -1).map((p) => p.coordinates),
    destination: points[points.length - 1]?.coordinates ?? null,
    segments: segments.map(({ coordinates, status }) => ({
      coordinates,
      status,
    })),
    position:
      underway?.current ??
      atPort?.coordinates ??
      (notDeparted ? (points[0]?.coordinates ?? null) : null),
  };
}

/** Share of the trip done today, from departure and arrival days. */
export function tripProgress(arrival: Arrival, today: Date) {
  // Arrived or due today: it is at the port, not on the way.
  if (arrival.arrived || arrival.offsetDays === null || arrival.offsetDays <= 0)
    return null;
  const departed = arrival.etd
    ? getShipmentCalendarDayOffset(arrival.etd, today)
    : null;
  const total = departed !== null ? arrival.offsetDays - departed : null;
  if (departed === null || total === null || total <= 0)
    // Without a departure date, place it by days left on a typical trip.
    return Math.min(
      1,
      Math.max(0.08, 1 - arrival.offsetDays / (arrival.mode === 'air' ? 3 : 30))
    );
  return Math.min(1, Math.max(0, -departed / total));
}

export function buildArrivalMap(
  arrivals: Arrival[],
  routes: Record<string, TrackingRoute>,
  today: Date
): ArrivalMap {
  const byPod = new Map<string, Arrival[]>();
  for (const arrival of arrivals)
    byPod.set(arrival.pod, [...(byPod.get(arrival.pod) ?? []), arrival]);

  const voyages: MapVoyage[] = [];
  const routedDestination = new Map<string, LngLat>();
  const origins = new Map<string, MapOrigin>();
  for (const [pod, podArrivals] of byPod) {
    const seen = new Set<string>();
    for (const arrival of podArrivals) {
      if (seen.has(arrival.containerKey)) continue;
      seen.add(arrival.containerKey);
      const journey = arrival.journey;
      const live = readRoute(journey ? routes[journey.id] : undefined);
      if (live.destination && !routedDestination.has(pod))
        routedDestination.set(pod, live.destination);
      const sameUnit = podArrivals.filter(
        (a) => a.containerKey === arrival.containerKey
      );
      const origin =
        live.origin ??
        place(journey?.origin_locode) ??
        place(arrival.mode === 'air' ? 'SCL' : 'CLSAI');
      if (origin) {
        const code = (
          (live.origin ? live.originCode : journey?.origin_locode) ??
          (journey?.origin_name ? '' : arrival.mode === 'air' ? 'SCL' : 'CLSAI')
        ).toUpperCase();
        const fullName = journey?.origin_name?.trim() || code;
        const name =
          ORIGIN_NAMES[code] ??
          (fullName.length > 22 && code ? code : fullName.slice(0, 22));
        const key = origin.join(',');
        if (!origins.has(key))
          origins.set(key, {
            key,
            name: name.toUpperCase(),
            coordinates: origin,
          });
      }
      const knownVessel = !/^Sin (nave|aerolínea)$/.test(arrival.vessel);
      voyages.push({
        key: arrival.containerKey,
        pod,
        routeKey: knownVessel
          ? JSON.stringify([arrival.vessel, arrival.voyage, pod])
          : arrival.containerKey,
        routeLabel: knownVessel
          ? arrival.mode === 'air' || arrival.voyage === '—'
            ? `${arrival.vessel} → ${arrival.portName}`
            : `${arrival.vessel} · Viaje ${arrival.voyage} → ${arrival.portName}`
          : `${arrival.container} → ${arrival.portName}`,
        originKey: origin ? origin.join(',') : null,
        arrivalIds: sameUnit.map((a) => a.id),
        tone: toneOf(sameUnit),
        label: [
          arrival.container,
          arrival.vessel,
          arrival.eta ? `ETA ${shortDate(arrival.eta)}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        origin,
        destination: live.destination,
        segments: live.segments,
        stops: live.stops,
        position: live.position,
        progress:
          live.segments.length || live.position
            ? null
            : tripProgress(arrival, today),
      });
    }
  }

  const destinations = [...byPod].map(([pod, podArrivals]) => {
    const first = podArrivals[0];
    const coordinates =
      routedDestination.get(pod) ??
      place(first.journey?.destination_locode) ??
      place(pod);
    return {
      pod,
      name: first.portName,
      flag: first.flag,
      coordinates,
      tone: toneOf(podArrivals),
      arrivals: podArrivals,
      kg: podArrivals.reduce((sum, a) => sum + a.kg, 0),
      minOffset: Math.min(...podArrivals.map((a) => a.offsetDays ?? Infinity)),
    };
  });
  const located = new Map(
    destinations.map((d) => [d.pod, d.coordinates] as const)
  );
  for (const voyage of voyages)
    voyage.destination ??= located.get(voyage.pod) ?? null;

  return {
    destinations: destinations.sort(
      (a, b) => a.minOffset - b.minOffset || b.kg - a.kg
    ),
    voyages,
    origins: [...origins.values()],
    live: voyages.filter((v) => v.segments.length || v.position).length,
    estimated: voyages.filter((v) => v.progress !== null).length,
  };
}

/** Whether a voyage belongs to what is picked on the map. */
export function voyageMatches(voyage: MapVoyage, filter: MapFilter) {
  return filter.kind === 'pod'
    ? voyage.pod === filter.key
    : filter.kind === 'origin'
      ? voyage.originKey === filter.key
      : voyage.routeKey === filter.key;
}

/** Arrival ids picked on the map; empty when the pick is no longer on it. */
export function mapFilterIds(map: ArrivalMap, filter: MapFilter) {
  if (filter.kind === 'pod')
    return new Set(
      map.destinations
        .find((destination) => destination.pod === filter.key)
        ?.arrivals.map((arrival) => arrival.id) ?? []
    );
  return new Set(
    map.voyages
      .filter((voyage) => voyageMatches(voyage, filter))
      .flatMap((voyage) => voyage.arrivalIds)
  );
}

/**
 * Center longitude that keeps every point on the same side of the map edge:
 * the edge goes through the widest empty band of longitudes.
 */
export function centerLongitude(points: LngLat[]) {
  const lngs = [...new Set(points.map(([lng]) => wrapLongitude(lng)))].sort(
    (a, b) => a - b
  );
  if (lngs.length < 2) return lngs[0] ?? -70;
  let gap = lngs[0] + 360 - lngs[lngs.length - 1];
  let edge = lngs[lngs.length - 1] + gap / 2;
  for (let i = 1; i < lngs.length; i++) {
    const width = lngs[i] - lngs[i - 1];
    if (width > gap) {
      gap = width;
      edge = lngs[i - 1] + width / 2;
    }
  }
  return wrapLongitude(edge + 180);
}
